import * as React from 'react';
import { IAthlete, IGroup, ITeam } from '../../../../data/types';
import { useListSearch } from '../../../../hooks/useListSearch';
import { FullHeightColumn } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { ScrollableArea } from '../../../layout/ScrollableArea';
import { ClearAllButton, SelectAllButton } from '../../../shared/Button';
import { Count } from '../../../shared/Count';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ListControlLabel } from '../../../shared/form/ListControlLabel';
import { SelectBox } from '../../../shared/form/SelectBox';
import { TextField } from '../../../shared/form/TextField';
import { ListControlContainer } from '../../../shared/List/ListControlContainer';
import { MultiAthleteSelectList } from '../athletes/MultiAthleteSelectList';
import { MultiTeamSelectList } from '../teams/MultiTeamSelectList';

interface ITeamManagementProps {
  teams: ITeam[];
  selectedTeams: ITeam[];
  teamSelectionHandler: (team: ITeam, checked: boolean) => void;
  clearAllTeams: () => void;
  selectAllTeams: () => void;
}

interface IAthleteManagementProps {
  athletes: IAthlete[];
  selectedAthletes: IAthlete[];
  athleteSelectionHandler: (athlete: IAthlete, checked: boolean) => void;
  clearAllAthletes: () => void;
  selectAllAthletes: () => void;
  aggregateOnCreate: boolean;
  setAggregateOnCreate: (value: boolean) => void;
}

interface IProps {
  group: IGroup;
  isEdit: boolean;
  onNameChange?: (name: string, value: string | number) => void;
}

const GroupForm = (
  props: IProps & ITeamManagementProps & IAthleteManagementProps
): React.ReactElement => {
  const nameChangeHandler = (value: string): void =>
    props.onNameChange('name', value);

  const {
    list: teams,
    search: searchTeams,
    clear: clearTeamsSearch,
    searchTerm: teamSearchTerm,
  } = useListSearch(props.teams);

  const {
    list: athletes,
    search: searchAthletes,
    clear: clearAthleteSearch,
    searchTerm: athleteSearchTerm,
  } = useListSearch(props.athletes);

  return (
    <Row shouldCollapse>
      <FullHeightColumn>
        <div
          style={{
            padding: '10px',
          }}
        >
          <ControlSpacer>
            <TextField
              name='name'
              placeholder='Enter Group Name'
              onChange={nameChangeHandler}
              value={props.group ? props.group.name : ''}
              label='Name'
              type='text'
            />
          </ControlSpacer>
        </div>
        {!props.isEdit && (
          <div
            style={{
              padding: '10px',
            }}
          >
            <SelectBox
              style={{ justifyContent: 'flex-start' }}
              label='Include Historical Data in Group'
              selected={props.aggregateOnCreate}
              onSelect={(e: React.ChangeEvent<HTMLInputElement>): void =>
                props.setAggregateOnCreate(e.target.checked)
              }
            />
          </div>
        )}
        <ListControlLabel
          label='Select Team(s)'
          helpText='A group must be assigned to at least one team'
          onSearchClearHandler={clearTeamsSearch}
          searchValue={teamSearchTerm}
          onSearchHandler={searchTeams}
        >
          <ClearAllButton
            onClick={props.clearAllTeams}
            disabled={props.selectedTeams.length === 0}
          />
          <SelectAllButton
            onClick={props.selectAllTeams}
            disabled={props.selectedTeams.length === props.teams.length}
          />
          <Count
            value={props.selectedTeams.length}
            style={{
              alignSelf: 'flex-end',
            }}
          />
        </ListControlLabel>
        <ListControlContainer>
          <ScrollableArea>
            <MultiTeamSelectList
              teams={teams}
              selectedTeams={props.selectedTeams}
              onSelect={props.teamSelectionHandler}
              emptyMessage={
                teamSearchTerm.length > 0
                  ? `No teams matching '${teamSearchTerm}'`
                  : null
              }
            />
          </ScrollableArea>
        </ListControlContainer>
      </FullHeightColumn>
      <FullHeightColumn
        style={{
          marginLeft: '10px',
        }}
      >
        <ListControlLabel
          label='Select Athletes (optional)'
          onSearchClearHandler={clearAthleteSearch}
          searchValue={athleteSearchTerm}
          onSearchHandler={searchAthletes}
        >
          <ClearAllButton
            onClick={props.clearAllAthletes}
            disabled={props.selectedAthletes.length === 0}
          />
          <SelectAllButton
            onClick={props.selectAllAthletes}
            disabled={props.selectedAthletes.length === props.athletes.length}
          />
          <Count
            value={props.selectedAthletes.length}
            style={{
              alignSelf: 'flex-end',
            }}
          />
        </ListControlLabel>
        <ListControlContainer>
          <ScrollableArea
            style={{
              flex: 1,
            }}
          >
            <MultiAthleteSelectList
              athletes={athletes}
              teams={props.teams}
              selectedTeams={props.selectedTeams}
              selectedAthletes={props.selectedAthletes}
              onSelect={props.athleteSelectionHandler}
              clearSelected={props.clearAllAthletes}
              disabled={!Boolean(props.selectedTeams.length)}
              emptyMessage={
                athleteSearchTerm.length > 0
                  ? `No athletes matching '${athleteSearchTerm}'`
                  : null
              }
            />
          </ScrollableArea>
        </ListControlContainer>
      </FullHeightColumn>
    </Row>
  );
};

export { GroupForm };
export default GroupForm;
